// Helix 0.2.0 does not export AlertBanner from its index; its re-exported Flowbite `Alert` (role=alert) is the fallback.
import { Alert, Button, Card, Spinner } from '@helix-ds/react'
import React, { useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { HrcConnectState, HrcFlowError } from '../hooks/useHrcConnect'
import { useHrcConnect } from '../hooks/useHrcConnect'
import type { HrcConnectSubject } from '../types'
import FastenStitch from './FastenStitch'
import HrcConnectedSystems from './HrcConnectedSystems'

const NO_SUBJECT_HELP_ID = 'hrc-no-subject-help'

/**
 * A status line the flow is still working on, with the app's spinner beside it.
 *
 * The spinner is decorative and `aria-hidden`: the text already carries `role="status"`,
 * so announcing the spinner too would just repeat it. Matches TestingProgressCard rather
 * than inventing a second spinner.
 */
const WorkingStatus: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-center gap-2">
    <Spinner color="info" size="sm" aria-hidden="true" className="shrink-0" />
    <p role="status" className="text-text-body text-sm dark:text-gray-300">
      {children}
    </p>
  </div>
)

/** A message id plus its values, so copy is chosen here and rendered by react-intl. */
type Message = { id: string; values?: Record<string, string | number> }

/**
 * Which error the user sees.
 *
 * ``retryAfterSeconds`` goes in as a NUMBER and the wait is phrased by an ICU plural in the
 * message, not assembled here: "in about 1 second" / "in about 5 seconds" is a plural rule,
 * and languages do not share ours. ``null`` becomes 0, which the message words as "in a
 * moment".
 */
function errorMessage(error: HrcFlowError): Message {
  switch (error.kind) {
    case 'start-failed':
      return { id: 'healthRecords.error.startFailed' }
    case 'widget-config':
      return { id: 'healthRecords.error.widgetConfig' }
    case 'no-connection':
      return { id: 'healthRecords.error.noConnection' }
    case 'retryable':
      return { id: 'healthRecords.error.retryable' }
    case 'in-progress':
      return {
        id: 'healthRecords.error.inProgress',
        values: { seconds: Math.max(error.retryAfterSeconds ?? 0, 0) },
      }
    case 'rate-limited':
      return {
        id: 'healthRecords.error.rateLimited',
        values: { seconds: Math.max(error.retryAfterSeconds ?? 0, 0) },
      }
    case 'vendor-unavailable':
      return { id: 'healthRecords.error.vendorUnavailable' }
    case 'session-expired':
      // Deliberately does NOT tell the user to sign in first: the HRC calls suppress
      // the unauthorized redirect, so nothing signs them out here. Retrying re-sends
      // the SAME Idempotency-Key; if the session really is dead, the token fetch on
      // that retry trips the client's reauth-required branch and logs out properly.
      return { id: 'healthRecords.error.sessionExpired' }
    case 'unconfirmed':
      return { id: 'healthRecords.error.unconfirmed' }
    case 'fatal':
      return {
        id:
          error.reason === 'revoked'
            ? 'healthRecords.error.fatalRevoked'
            : error.reason === 'expired'
              ? 'healthRecords.error.fatalExpired'
              : error.reason === 'conflict'
                ? 'healthRecords.error.fatalConflict'
                : 'healthRecords.error.fatalDefault',
      }
  }
}

/** Retryable errors re-send the same request; everything else needs a fresh start. */
function recoveryFor(error: HrcFlowError): 'retry' | 'start-again' | 'start-over' {
  switch (error.kind) {
    case 'retryable':
    case 'unconfirmed':
    case 'in-progress':
    case 'rate-limited':
    case 'vendor-unavailable':
      return 'retry'
    case 'session-expired':
      return error.resume === 'retry' ? 'retry' : 'start-again'
    case 'start-failed':
      return 'start-again'
    default:
      return 'start-over'
  }
}

/** A stable key for "what the user is looking at", so focus moves when (and only when) it changes. */
function viewKey(state: HrcConnectState): string {
  return state.phase === 'error' ? `error:${state.error.kind}` : state.phase
}

/**
 * Whose records this card is for, as a message VARIANT rather than an English fragment.
 *
 * This used to return a possessive ("your" / "Casey's") that three sentences spliced in.
 * That only works in English: Chinese has no "'s", and other languages reorder the clause
 * entirely. So each sentence gets its own full message per variant, and the name — which is
 * never translated — travels as a value.
 */
type SubjectVariant = 'Self' | 'Named' | 'Child'

function subjectVariant(subject: HrcConnectSubject): { variant: SubjectVariant; name: string } {
  const name = subject.name?.trim()
  if (subject.role === 'SELF') return { variant: 'Self', name: '' }
  return name ? { variant: 'Named', name } : { variant: 'Child', name: '' }
}

type Props = {
  /**
   * Who this card connects records for. A prop, never the signed-in user's token claim:
   * that claim is the CONSENTER, so reading it here is what made the card say "your
   * child's health records" over a flow that connected the guardian's own.
   */
  subject: HrcConnectSubject
  /**
   * A connection was saved, so anything derived from the roster is now stale. The card
   * does not fetch — the section owns that — it only says when there is something new.
   */
  onConnected?: () => void
}

const HrcConnectCard: React.FC<Props> = ({ subject, onConnected }) => {
  const recordSubjectPrincipalId = subject.recordSubjectPrincipalId
  const intl = useIntl()
  const { variant, name } = subjectVariant(subject)
  const who = name ? { name } : undefined
  const {
    state,
    start,
    retry,
    onWidgetComplete,
    onWidgetClose,
    onWidgetMalformed,
    onWidgetConfigError,
    malformedSeen,
    canStart,
  } = useHrcConnect(recordSubjectPrincipalId)
  const regionRef = useRef<HTMLDivElement | null>(null)
  const key = viewKey(state)

  // Recovery copy belongs to a FAILED auto-open, not to `ready` as such. Tracked against the
  // session's nonce rather than as a bare boolean so a new session (Start over mints a fresh
  // handoff) cannot inherit the previous one's failure.
  const nonce = state.phase === 'ready' ? state.session.handoffNonce : null
  const [autoOpenFailedFor, setAutoOpenFailedFor] = useState<string | null>(null)
  const autoOpenFailed = nonce !== null && autoOpenFailedFor === nonce

  // Every transition unmounts the control that had focus; move it to the live region instead of <body>.
  useEffect(() => {
    if (state.phase !== 'idle') regionRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` already encodes everything that matters
  }, [key])

  // Once per ARRIVAL at `connected`, not once per card. `connected` survives re-renders and
  // the listener refetches, so firing on every render would put this card in a loop with its
  // own parent -- but a guard that never resets is just as wrong in the other direction: the
  // card can reach `connected` again (Connect another health system), and that second save
  // would be stored and never shown.
  const announced = useRef(false)
  useEffect(() => {
    if (state.phase !== 'connected') {
      announced.current = false
      return
    }
    if (announced.current) return
    announced.current = true
    onConnected?.()
  }, [state.phase, onConnected])

  const busy = state.phase === 'starting' || state.phase === 'submitting'

  return (
    <Card padding="lg" role="region" aria-labelledby="hrc-connect-heading">
      <div className="flex flex-col gap-1">
        <p className="text-text-body text-sm font-medium tracking-wider uppercase dark:text-gray-400">
          <FormattedMessage id={`healthRecords.card.eyebrow${variant === 'Self' ? 'Self' : 'Guardian'}`} />
        </p>
        <h2 id="hrc-connect-heading" className="text-brand-600 dark:text-brand-300 text-2xl leading-8 font-semibold">
          <FormattedMessage id={`healthRecords.card.heading${variant}`} values={who} />
        </h2>
        <p className="text-text-body text-base dark:text-gray-300">
          <FormattedMessage id={`healthRecords.card.subtitle${variant}`} values={who} />
        </p>
      </div>

      {/* Above the flow, not inside it: this is what sharing is already in place, and it
          stays legible while a new connection is being made. */}
      {subject.healthSystems?.length ? (
        <div className="mt-6">
          <HrcConnectedSystems systems={subject.healthSystems} />
        </div>
      ) : null}

      <div
        ref={regionRef}
        role="group"
        aria-label={intl.formatMessage({ id: 'healthRecords.card.statusRegion' })}
        tabIndex={-1}
        className="focus-visible:ring-brand-600/20 mt-6 flex flex-col gap-4 rounded-xl focus:outline-none focus-visible:ring-2"
      >
        {state.phase === 'idle' || state.phase === 'starting' ? (
          <div className="flex flex-col gap-2">
            <div>
              <Button
                variant="primary"
                className="cursor-pointer rounded-full"
                onClick={() => void start()}
                disabled={busy || !canStart}
                aria-describedby={canStart ? undefined : NO_SUBJECT_HELP_ID}
              >
                <FormattedMessage id="healthRecords.card.shareRecords" />
              </Button>
            </div>
            {!canStart ? (
              <p id={NO_SUBJECT_HELP_ID} className="text-text-body text-sm dark:text-gray-300">
                <FormattedMessage id="healthRecords.card.noSubject" />
              </p>
            ) : null}
          </div>
        ) : null}

        {state.phase === 'starting' ? (
          <WorkingStatus>
            <FormattedMessage id="healthRecords.card.preparing" />
          </WorkingStatus>
        ) : null}

        {state.phase === 'submitting' ? (
          <WorkingStatus>
            <FormattedMessage id="healthRecords.card.submitting" />
          </WorkingStatus>
        ) : null}

        {/* The health system confirms to us a few seconds after the window closes. That wait is
            expected, so it reads as progress and asks nothing of the family user. */}
        {state.phase === 'waiting' ? (
          <WorkingStatus>
            <FormattedMessage id="healthRecords.card.waiting" />
          </WorkingStatus>
        ) : null}

        {state.phase === 'ready' ? (
          <>
            {/* Until the auto-open gives up, the modal is on its way and covers this card.
                Offering "Start over" then is noise at best, and at worst invites a restart of
                a flow that is about to work. */}
            {autoOpenFailed ? (
              <p role="status" className="text-text-body text-sm dark:text-gray-300">
                <FormattedMessage id="healthRecords.card.readyManual" />
              </p>
            ) : (
              <WorkingStatus>
                <FormattedMessage id="healthRecords.card.readyOpening" />
              </WorkingStatus>
            )}
            <FastenStitch
              autoOpen
              publicId={state.session.publicId}
              externalId={state.session.handoffNonce}
              onComplete={onWidgetComplete}
              onClose={onWidgetClose}
              onMalformed={onWidgetMalformed}
              onConfigError={onWidgetConfigError}
              onAutoOpenFailed={() => setAutoOpenFailedFor(nonce)}
            />
            {malformedSeen ? (
              <p role="status" className="text-text-body text-sm dark:text-gray-300">
                <FormattedMessage id="healthRecords.card.malformed" />
              </p>
            ) : null}
            {autoOpenFailed || malformedSeen ? (
              <div>
                <Button variant="secondary" className="cursor-pointer" onClick={() => void start()}>
                  <FormattedMessage id="healthRecords.card.startOver" />
                </Button>
              </div>
            ) : null}
          </>
        ) : null}

        {state.phase === 'closed' ? (
          <>
            <p role="status" className="text-text-body text-sm dark:text-gray-300">
              <FormattedMessage id="healthRecords.card.closed" />
            </p>
            <div>
              <Button variant="secondary" className="cursor-pointer" onClick={() => void start()}>
                <FormattedMessage id="healthRecords.card.startOver" />
              </Button>
            </div>
          </>
        ) : null}

        {state.phase === 'connected' ? (
          <>
            <Alert color="success" role="status">
              <FormattedMessage id={`healthRecords.card.connected${variant}`} values={who} />
            </Alert>
            {/* Only when the widget dropped some: it explains a LOSS, and showing it after a
                clean save would invent one. */}
            {state.additionalConnections > 0 ? (
              <p className="text-text-body text-sm dark:text-gray-300">
                <FormattedMessage
                  id="healthRecords.card.extrasNotSaved"
                  values={{ count: state.additionalConnections }}
                />
              </p>
            ) : null}
            {/* Always offered, not just after a dropped connection. A family can connect more
                than one health system, and this card now lists them -- so finishing a clean
                save with no way to add a second one is a dead end. */}
            <div>
              <Button variant="secondary" className="cursor-pointer" onClick={() => void start()}>
                <FormattedMessage id="healthRecords.connectAnother" defaultMessage="Connect another health system" />
              </Button>
            </div>
          </>
        ) : null}

        {state.phase === 'error' ? (
          <>
            {state.error.kind === 'fatal' && state.error.reason === 'revoked' ? (
              <Alert color="info" role="status">
                <FormattedMessage {...errorMessage(state.error)} />
              </Alert>
            ) : (
              <Alert color="failure">
                <FormattedMessage {...errorMessage(state.error)} />
              </Alert>
            )}
            <div>
              <Button
                variant="secondary"
                className="cursor-pointer"
                onClick={recoveryFor(state.error) === 'retry' ? retry : () => void start()}
              >
                <FormattedMessage
                  id={
                    recoveryFor(state.error) === 'start-over'
                      ? 'healthRecords.card.startOver'
                      : 'healthRecords.card.tryAgain'
                  }
                />
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </Card>
  )
}

export default HrcConnectCard
